import { useOrderBumbalData } from "app/hooks/appointment/useOrderBumbalData";
import { useOrderContext } from "app/providers/OrderProvider";
import StatusGif from "entities/status-gif";
import moment from "moment";
import { useMemo } from "react";
import Confetti from "shared/ui/confetti";
import Heading from "shared/ui/typography/Heading";
import Text from "shared/ui/typography/Text";
import { isTestEnvironment } from "shared/utils/checkEnvironment";
import { twMerge } from "tailwind-merge";
import StatusSupport from "widgets/status-support";

const APPOINTMENT_TYPES: Record<
  string,
  { title: string; description: string }
> = {
  measurement: {
    title: "Je hebt al een inmeetafspraak gemaakt!",
    description: `Jouw voorkeuren zijn bij ons bekend. Je ontvangt hiervan een bevestiging per mail. 
    Hou er met het plannen rekening mee dat de situatie waarin jouw wand of deur wordt geplaatst 
    helemaal gereed is voor inmeten. Denk hierbij aan het leggen van een nieuwe vloer, het stuken 
    van de wand en een definitieve afwerking van de sparing of het kozijn. Neem bij twijfel contact 
    op met onze klantenservice en hou altijd je referentienummer bij de hand.`,
  },
  shipment: {
    title: "Je hebt al een afleverafspraak gemaakt!",
    description: `Jouw voorkeuren zijn bij ons bekend. Je ontvangt hiervan een bevestiging per mail. 
      Hou er met het plannen rekening mee dat de situatie waarin jouw wand of deur wordt geplaatst 
      helemaal gereed is voor inmeten. Denk hierbij aan het leggen van een nieuwe vloer, het stuken 
      van de wand en een definitieve afwerking van de sparing of het kozijn. Neem bij twijfel contact 
      op met onze klantenservice en hou altijd je referentienummer bij de hand.`,
  },
  placement: {
    title: "Je hebt al een montage afspraak gemaakt!",
    description: "",
  },
};

const HasAppointment = () => {
  const { order, type, isOrderFetching, orderError } = useOrderContext();
  const { orderBumbalData } = useOrderBumbalData();

  const TypeBasedComponent = useMemo(() => {
    const bumbalDate = new Date(orderBumbalData!.date);
    switch (type) {
      case "placement":
        return () => (
          <div>
            <Text className="gg-mb-4">
              Je hebt een afspraak ingepland voor levering en montage, leuk! Wij
              gaan aan de slag met je bestelling en we zien je op{" "}
              <b>
                {bumbalDate.toLocaleDateString("nl-NL", {
                  weekday: "long",
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                })}
              </b>
              . Tot dan!
            </Text>

            {!isOrderFetching && !orderError && (
              <Text className="gg-mb-4">
                Een bevestiging van de afspraak wordt per e-mail verzonden. Heb
                je vragen? Neem dan contact met ons op en houd je
                referentienummer{" "}
                <span className="gg-font-bold">{order?.orderNumber}</span> en/of
                postcode + huisnummer gereed.
              </Text>
            )}
          </div>
        );
      default:
        return () => (
          <div>
            <Text className="gg-mb-4 gg-max-w-screen-md">
              {APPOINTMENT_TYPES[type].description}
            </Text>

            {!isOrderFetching && !orderError && !!order && (
              <Text>Referentienummer: {order?.orderNumber}</Text>
            )}

            <Text>
              Ingepland op: {moment(orderBumbalData?.date).format("DD-MM-YYYY")}
            </Text>
          </div>
        );
    }
  }, [type]);

  return (
    <div
      className={twMerge(
        isTestEnvironment()
          ? "gg-mx-auto gg-px-4 gg-py-14 sm:gg-px-14 lg:gg-px-0"
          : "",
        "gg-max-w-3xl",
      )}
    >
      <Confetti />

      <div className="gg-mb-14">
        <StatusGif
          src={`https://media1.giphy.com/media/ua4OxpbgAtItnS5hWu/giphy.gif`}
          alt="Een GewoonGers Gif. Een monteur springt uit zijn bus."
        />

        <Heading
          headingLevel="h1"
          className="gg-mb-3 md:gg-mb-5 md:gg-text-4xl lg:gg-text-5xl"
        >
          {APPOINTMENT_TYPES[type].title}
        </Heading>

        <TypeBasedComponent />
      </div>

      <StatusSupport />
    </div>
  );
};
export default HasAppointment;
